import React, { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches ||
                            window.navigator.standalone === true;

/**
 * Small "Instalar app" button that appears in the sidebar when the browser
 * fires `beforeinstallprompt` (Android/Chrome/Edge) or when we detect iOS Safari
 * (which requires manual "Añadir a pantalla de inicio").
 * Hidden entirely when the app is already running as a PWA.
 */
const InstallButton = () => {
  const [canPrompt, setCanPrompt] = useState(!!window.__deferredInstallPrompt);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('sf_install_dismissed') === '1');
  const [running, setRunning] = useState(isStandalone());

  useEffect(() => {
    const onAvail = () => setCanPrompt(true);
    const onInstalled = () => { setRunning(true); setCanPrompt(false); };
    window.addEventListener('sf:install-available', onAvail);
    window.addEventListener('sf:installed', onInstalled);
    return () => {
      window.removeEventListener('sf:install-available', onAvail);
      window.removeEventListener('sf:installed', onInstalled);
    };
  }, []);

  const install = async () => {
    // iOS: no programmatic prompt available — show the hint sheet.
    if (isIOS() && !window.__deferredInstallPrompt) {
      setShowIosHint(true);
      return;
    }
    const p = window.__deferredInstallPrompt;
    if (!p) return;
    p.prompt();
    try { await p.userChoice; } catch { /* noop */ }
    window.__deferredInstallPrompt = null;
    setCanPrompt(false);
  };

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem('sf_install_dismissed', '1');
  };

  if (running || dismissed) return null;
  if (!canPrompt && !isIOS()) return null; // desktop Chrome may take a while; hide if not eligible

  return (
    <>
      <div className="px-3 pb-3" data-testid="install-cta">
        <div className="relative bg-gradient-to-br from-[#A3FF12]/15 to-[#A3FF12]/5 border border-[#A3FF12]/30 rounded-xl p-3">
          <button onClick={dismiss} className="absolute top-1.5 right-1.5 text-white/40 hover:text-white p-0.5" title="Descartar">
            <X size={12}/>
          </button>
          <div className="flex items-center gap-2 mb-1.5">
            <Download size={14} className="text-[#A3FF12]"/>
            <div className="text-white font-black text-xs">Instalar app</div>
          </div>
          <p className="text-white/60 text-[10px] leading-snug mb-2">
            {isIOS()
              ? 'Añádela a tu pantalla de inicio para abrirla como una app nativa.'
              : 'Instala Adquiriam como app: apertura instantánea y trabaja incluso sin cobertura.'}
          </p>
          <button onClick={install} data-testid="btn-install-pwa"
            className="w-full bg-[#A3FF12] text-black font-black text-[11px] py-1.5 rounded-lg hover:bg-[#b8ff3a] transition-colors">
            {isIOS() ? 'Cómo instalarla' : 'Instalar ahora'}
          </button>
        </div>
      </div>

      {showIosHint && (
        <div className="fixed inset-0 z-[1000] bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIosHint(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/15 rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">INSTALAR EN iOS</div>
                <h3 className="text-white font-black text-lg mt-1">Añadir a pantalla de inicio</h3>
              </div>
              <button onClick={() => setShowIosHint(false)} className="text-white/40 hover:text-white"><X size={20}/></button>
            </div>
            <ol className="space-y-3 text-sm text-white/80">
              <li className="flex gap-2">
                <span className="text-[#A3FF12] font-black">1.</span>
                <span>Pulsa el botón <Share size={14} className="inline mx-1 text-blue-400"/> <b>Compartir</b> en la barra inferior de Safari.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#A3FF12] font-black">2.</span>
                <span>Baja y elige <b>«Añadir a pantalla de inicio»</b>.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#A3FF12] font-black">3.</span>
                <span>Pulsa <b>«Añadir»</b> arriba a la derecha. Listo.</span>
              </li>
            </ol>
            <p className="text-white/40 text-[11px] mt-4">La app se abre a pantalla completa, sin barra de navegación, como una nativa.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallButton;
